import React, { useState } from 'react';
import { countries } from '../../data/countries';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const capFirst = (val: string) =>
  val.length === 0 ? val : val.charAt(0).toUpperCase() + val.slice(1);

interface Props {
  onClose: () => void;
  onCreated?: () => void;
}

const AddBowlerModalAdmin: React.FC<Props> = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', country: '', city: '', gender: '', dob: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [existingBowler, setExistingBowler] = useState<{
    name: string; email: string | null; country: string; city: string;
  } | null>(null);
  const [similarBowlers, setSimilarBowlers] = useState<Array<{ id: number; name: string; email: string | null }>>([]);

  const fullName = () => `${form.firstName.trim()} ${form.lastName.trim()}`.trim();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const val = (name === 'firstName' || name === 'lastName') ? capFirst(value) : value;
    setForm(prev => ({ ...prev, [name]: val }));
    setError('');
  };

  const reset = () => {
    setForm({ firstName: '', lastName: '', email: '', country: '', city: '', gender: '', dob: '' });
    setError('');
    setExistingBowler(null);
    setSimilarBowlers([]);
    setSuccess(false);
  };

  const doCreate = async () => {
    setSubmitting(true);
    setError('');
    try {
      const headers = { 'Content-Type': 'application/json', ...authHeader() };
      const createRes = await fetch(`${API}/admin/invites/create-bowler`, {
        method: 'POST', headers,
        body: JSON.stringify({
          fullName: fullName(),
          email: form.email.trim() || null,
          country: form.country || '',
          city: form.city.trim() || '',
          gender: form.gender || null,
          dob: form.dob || null,
        }),
      });
      const createData = await createRes.json();
      if (!createRes.ok || !createData.success) {
        if (createData.exists) { setExistingBowler(createData.bowler); return; }
        setError(createData.error || 'Failed to create bowler.');
        return;
      }
      setSuccess(true);
      onCreated?.();
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.firstName.trim() || !form.lastName.trim()) {
      setError('First and last name are required.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const headers = { 'Content-Type': 'application/json', ...authHeader() };

      if (form.email.trim()) {
        const checkRes = await fetch(`${API}/admin/invites/check-bowler`, {
          method: 'POST', headers,
          body: JSON.stringify({ email: form.email.trim() }),
        });
        const checkData = await checkRes.json();
        if (!checkRes.ok) { setError(checkData.error || 'Error checking email.'); return; }
        if (checkData.exists) { setExistingBowler(checkData.bowler); return; }
      }

      const simRes = await fetch(`${API}/admin/invites/similar-bowlers`, {
        method: 'POST', headers,
        body: JSON.stringify({ name: fullName() }),
      });
      const simData = await simRes.json();
      if (simData.success && simData.bowlers && simData.bowlers.length > 0) {
        setSimilarBowlers(simData.bowlers);
        return;
      }

      await doCreate();
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
      <div className="bg-gradient-to-br from-slate-800 to-slate-900 border border-white/20 rounded-2xl p-6 max-w-md w-full shadow-2xl">
        {success ? (
          <div className="text-center">
            <div className="text-5xl mb-4">✅</div>
            <h3 className="text-xl font-bold text-white mb-2">Bowler Added!</h3>
            <p className="text-emerald-200 text-sm mb-6">The bowler account has been created successfully.</p>
            <div className="flex gap-3">
              <button onClick={reset}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-2.5 rounded-xl transition">
                Add Another
              </button>
              <button onClick={onClose}
                className="flex-1 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold py-2.5 rounded-xl transition">
                Close
              </button>
            </div>
          </div>
        ) : existingBowler ? (
          <div className="text-center">
            <div className="text-5xl mb-4">⚠️</div>
            <h3 className="text-xl font-bold text-white mb-2">Bowler Already Exists</h3>
            <div className="bg-white/5 rounded-xl p-4 space-y-2 mb-4 border border-white/10 text-left">
              <p className="text-white text-sm"><span className="text-blue-300 font-semibold">Name: </span>{existingBowler.name}</p>
              <p className="text-white text-sm"><span className="text-blue-300 font-semibold">Email: </span>{existingBowler.email || 'No email'}</p>
              {existingBowler.country && <p className="text-white text-sm"><span className="text-blue-300 font-semibold">Country: </span>{existingBowler.country}</p>}
              {existingBowler.city && <p className="text-white text-sm"><span className="text-blue-300 font-semibold">City: </span>{existingBowler.city}</p>}
            </div>
            <p className="text-orange-300 text-xs mb-4">This bowler is already in the system. You can search for them to invite directly.</p>
            <button onClick={() => setExistingBowler(null)}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 rounded-xl transition">
              Back
            </button>
          </div>
        ) : similarBowlers.length > 0 ? (
          <div>
            <div className="text-center mb-4">
              <div className="text-4xl mb-2">🔍</div>
              <h3 className="text-lg font-bold text-white">Similar Bowlers Found</h3>
              <p className="text-yellow-300 text-xs mt-1">We found bowlers with similar names. If any of these is the person you're adding, please cancel.</p>
            </div>
            <div className="space-y-2 mb-4 max-h-44 overflow-y-auto">
              {similarBowlers.map(b => (
                <div key={b.id} className="bg-white/5 border border-white/10 rounded-xl px-3 py-2 flex items-center justify-between">
                  <div>
                    <p className="text-white text-sm font-semibold">{b.name}</p>
                    <p className="text-blue-300 text-xs">{b.email || 'No email'}</p>
                  </div>
                  <span className="text-gray-400 text-xs">ID #{b.id + 100}</span>
                </div>
              ))}
            </div>
            <div className="flex gap-3">
              <button onClick={() => setSimilarBowlers([])}
                className="flex-1 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold py-2.5 rounded-xl transition">
                Cancel
              </button>
              <button onClick={() => { setSimilarBowlers([]); doCreate(); }} disabled={submitting}
                className="flex-1 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 disabled:opacity-50 text-white font-bold py-2.5 rounded-xl transition">
                {submitting ? 'Creating...' : 'No, Add Bowler'}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-white">+ New Bowler to System</h3>
              <button onClick={onClose} className="text-gray-400 hover:text-white text-xl leading-none">✕</button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-white text-sm font-semibold mb-1.5">First Name <span className="text-red-400">*</span></label>
                  <input type="text" name="firstName" value={form.firstName} onChange={handleChange}
                    placeholder="First"
                    className="w-full bg-white/10 border border-white/20 text-white placeholder-gray-400 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                    required />
                </div>
                <div>
                  <label className="block text-white text-sm font-semibold mb-1.5">Last Name <span className="text-red-400">*</span></label>
                  <input type="text" name="lastName" value={form.lastName} onChange={handleChange}
                    placeholder="Last"
                    className="w-full bg-white/10 border border-white/20 text-white placeholder-gray-400 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                    required />
                </div>
              </div>
              <div>
                <label className="block text-white text-sm font-semibold mb-1.5">Email <span className="text-gray-400 font-normal text-xs">(optional)</span></label>
                <input type="email" name="email" value={form.email} onChange={handleChange}
                  placeholder="Enter email address (recommended)"
                  className="w-full bg-white/10 border border-white/20 text-white placeholder-gray-400 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-400 transition" />
              </div>
              <div>
                <label className="block text-white text-sm font-semibold mb-1.5">Country <span className="text-gray-400 font-normal text-xs">(optional)</span></label>
                <select name="country" value={form.country} onChange={handleChange}
                  className="w-full bg-slate-700 border border-white/20 text-white rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-400 transition">
                  <option value="">— Select country —</option>
                  {countries.map(c => <option key={c.code} value={c.name}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-white text-sm font-semibold mb-1.5">City <span className="text-gray-400 font-normal text-xs">(optional)</span></label>
                <input type="text" name="city" value={form.city} onChange={handleChange}
                  placeholder="Enter city"
                  className="w-full bg-white/10 border border-white/20 text-white placeholder-gray-400 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-400 transition" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-white text-sm font-semibold mb-1.5">Gender <span className="text-gray-400 font-normal text-xs">(optional)</span></label>
                  <select name="gender" value={form.gender} onChange={handleChange}
                    className="w-full bg-slate-700 border border-white/20 text-white rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-400 transition">
                    <option value="">— Not specified —</option>
                    <option value="M">Male</option>
                    <option value="F">Female</option>
                  </select>
                </div>
                <div>
                  <label className="block text-white text-sm font-semibold mb-1.5">Date of Birth <span className="text-gray-400 font-normal text-xs">(optional)</span></label>
                  <input type="date" name="dob" value={form.dob} onChange={handleChange}
                    max={new Date().toISOString().split('T')[0]}
                    className="w-full bg-white/10 border border-white/20 text-white rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-400 transition [color-scheme:dark]" />
                </div>
              </div>
              {error && (
                <div className="bg-red-900/40 border border-red-400/50 text-red-200 rounded-xl px-4 py-3 text-sm">{error}</div>
              )}
              <div className="flex gap-3 pt-1">
                <button type="button" onClick={onClose}
                  className="flex-1 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold py-2.5 rounded-xl transition">
                  Cancel
                </button>
                <button type="submit" disabled={submitting}
                  className="flex-1 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 disabled:opacity-50 text-white font-bold py-2.5 rounded-xl transition">
                  {submitting ? 'Creating...' : 'Create Bowler'}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default AddBowlerModalAdmin;
